import {
  MetricHelpIconTooltip,
} from "@/components/ui/metric-tooltip";
import { cn } from "@/lib/utils";
import React, { ReactNode } from "react";

interface TradeLogLiftedTitleCardProps {
  title: string;
  children: ReactNode;
  /** Responsive pixel heights, `"100%"` to fill parent, or `"auto"` to grow with content. */
  height?: { base: string; sm: string; md: string } | string;
  tooltip?: string;
  timeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
  backgroundGradient?: string;
}

const TradeLogLiftedTitleCard: React.FC<TradeLogLiftedTitleCardProps> = ({
  title,
  children,
  height: heightProp,
  tooltip,
  timeframe,
  onTimeframeChange,
  backgroundGradient,
}) => {
  const heightStyle = heightProp === "100%"
    ? { height: "100%" }
    : typeof heightProp === "object"
      ? undefined
      : undefined;

  const responsiveClass =
    heightProp === "auto"
      ? "min-h-[90px] h-auto sm:min-h-[100px] md:min-h-[95px]"
      : !heightProp || heightProp === undefined
        ? "h-[90px] sm:h-[100px] md:h-[95px]"
        : typeof heightProp === "string" && heightProp === "100%"
          ? "h-full"
          : "";

  const responsiveStyle = typeof heightProp === "object"
    ? undefined
    : heightStyle;

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2 rounded-[6px] border border-border bg-card px-0 pt-2 pb-0 shadow-none ring-0 dark:bg-sidebar dark:text-sidebar-foreground",
        heightProp === "auto" ? "overflow-visible" : "overflow-hidden",
        responsiveClass
      )}
      style={{
        ...responsiveStyle,
        ...(typeof heightProp === "object" ? { height: undefined } : {}),
      }}
      data-trade-log-lifted-card=""
    >
      {/* Title bar — definition tooltip only on the help icon, not the title/card. */}
      <div
        data-trade-log-mini-stat-title-bar=""
        className="flex min-w-0 shrink-0 items-center justify-between gap-2 px-2"
      >
        <span
          data-trade-log-mini-stat-title=""
          className="m-0 min-w-0 flex-1 truncate text-left font-[family-name:var(--font-primary)] text-sm font-medium uppercase leading-tight text-muted-foreground dark:text-sidebar-muted-foreground"
        >
          {title}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {onTimeframeChange ? (
            <select
              value={timeframe}
              onChange={(e) => onTimeframeChange(e.target.value)}
              className="w-auto min-w-[70px] border-none bg-transparent font-ibm-plex text-xs font-medium uppercase text-muted-foreground dark:text-sidebar-muted-foreground focus:outline-none focus:ring-0 sm:min-w-[80px] sm:text-xs md:min-w-[90px] md:text-sm"
            >
              <option value="today">Today</option>
              <option value="7-days">7 days</option>
              <option value="14-days">14 days</option>
              <option value="30-days">30 days</option>
              <option value="all-time">All time</option>
            </select>
          ) : null}
          {tooltip ? <MetricHelpIconTooltip description={tooltip} label={title} /> : null}
        </div>
      </div>

      {/* Inner content */}
      <div
        className={cn(
          "relative flex min-h-0 flex-1 flex-col",
          heightProp === "auto" ? "overflow-visible" : "overflow-hidden",
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default TradeLogLiftedTitleCard;
